/**
 * Locale store for the customer storefront. Persisted in localStorage so the
 * user's last choice survives reloads. Fires a change event that the app
 * root subscribes to in order to reload CMS translations.
 */

import type { StorefrontLocaleConfig } from '@vastra/types';
import {
  DEFAULT_ADDITIONAL_STOREFRONT_LOCALES,
  DEFAULT_STOREFRONT_LOCALE,
  getStorefrontLocaleCodes,
  getStorefrontLocaleOptions,
  resolveAdditionalStorefrontLocales,
} from '@vastra/constants';

export type Locale = string;
export const DEFAULT_LOCALE = DEFAULT_STOREFRONT_LOCALE;

const STORAGE_KEY = 'vastra.locale';
const EVENT = 'vastra:locale-change';
const LOCALES_CONFIGURED_EVENT = 'vastra:storefront-locales-configured';

let additionalLocales: StorefrontLocaleConfig[] = [...DEFAULT_ADDITIONAL_STOREFRONT_LOCALES];

export function configureAdditionalStorefrontLocales(configured: StorefrontLocaleConfig[] | undefined): void {
  additionalLocales = resolveAdditionalStorefrontLocales(configured).filter(
    (l) => l.code !== DEFAULT_STOREFRONT_LOCALE,
  );
  const supported = getStorefrontLocaleCodes(additionalLocales);
  const current = readLocaleRaw();
  if (!supported.includes(current)) {
    writeLocale(DEFAULT_LOCALE);
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(LOCALES_CONFIGURED_EVENT));
  }
}

export function getStorefrontLocalesForSwitcher(): StorefrontLocaleConfig[] {
  return getStorefrontLocaleOptions(additionalLocales);
}

export function subscribeStorefrontLocalesConfigured(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(LOCALES_CONFIGURED_EVENT, cb);
  return () => window.removeEventListener(LOCALES_CONFIGURED_EVENT, cb);
}

function supportedCodes(): string[] {
  return getStorefrontLocaleCodes(additionalLocales);
}

function isSupportedLocale(v: unknown): v is string {
  return typeof v === 'string' && supportedCodes().includes(v);
}

function readLocaleRaw(): string {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (isSupportedLocale(raw)) return raw;
  } catch {
    // Access may throw in privacy mode; fall through.
  }
  if (typeof navigator !== 'undefined') {
    const lang = (navigator.language || '').toLowerCase();
    for (const code of supportedCodes()) {
      if (code !== DEFAULT_LOCALE && lang.startsWith(code)) return code;
    }
  }
  return DEFAULT_LOCALE;
}

export function readLocale(): Locale {
  return readLocaleRaw();
}

export function writeLocale(locale: Locale): void {
  if (!isSupportedLocale(locale)) return;
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Ignore quota / privacy-mode failures.
  }
  try {
    window.dispatchEvent(new CustomEvent<Locale>(EVENT, { detail: locale }));
  } catch {
    // Ignore browsers that don't allow CustomEvent construction.
  }
}

export function subscribeLocale(cb: (locale: Locale) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = (e: Event): void => {
    const detail = (e as CustomEvent<Locale>).detail;
    if (isSupportedLocale(detail)) cb(detail);
  };
  window.addEventListener(EVENT, handler as EventListener);
  return () => window.removeEventListener(EVENT, handler as EventListener);
}
